/**
 * Provider-facing language hook.
 *
 * Wraps i18next so components can read the active language, the list of
 * supported locales, and change the language.
 *
 * `setLanguage()`:
 *  1. normalizes the requested tag (unsupported tags are ignored),
 *  2. immediately switches i18next (which also persists to localStorage via the
 *     `languageChanged` listener in `./index`),
 *  3. best-effort syncs the choice to the authenticated user/provider
 *     preference endpoint via the locale-sync bridge (see `./localeSync`).
 *
 * Steps 1–2 are local and always succeed (offline/PWA-safe); step 3 is
 * fire-and-forget and never blocks or throws.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES, getIntlLocale, normalizeLocale } from './locales';
import { syncPreferredLocale } from './localeSync';

export interface UseLanguageResult {
  /** Active app locale code (BCP 47), e.g. `es-US`. */
  language: string;
  /** Active Intl/Luxon locale string for date/number formatting. */
  intlLocale: string;
  /** The four supported locales for rendering selectors. */
  locales: typeof SUPPORTED_LOCALES;
  /** Change the active language; unsupported tags are normalized/ignored. */
  setLanguage: (code: string) => void;
}

export const useLanguage = (): UseLanguageResult => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const setLanguage = useCallback(
    (code: string) => {
      const normalized = normalizeLocale(code);
      // Ignore unsupported tags entirely — neither switch nor sync.
      if (!normalized) return;

      if (normalized !== i18n.language) {
        void i18n.changeLanguage(normalized);
      }

      // Persist server-side when authenticated (best-effort, non-blocking).
      syncPreferredLocale(normalized);
    },
    [i18n],
  );

  return {
    language,
    intlLocale: getIntlLocale(language),
    locales: SUPPORTED_LOCALES,
    setLanguage,
  };
};
